/**
 * Preloader utilities for performance optimization
 * Following the studio pattern for data preloading and deferred loading
 */

import { mutate } from "swr";

import { callLambda, SERVICE_1 } from "./lambda";
import { getSeriesInfo } from "./series";
import {
  SWR_SERIES_PRIVATE_KEY,
  SWR_ACCOUNT_INFO_KEY
} from "../services/swr_keys";

/**
 * Safe requestIdleCallback with fallback to setTimeout
 * Used for deferring non-critical operations
 */
export const safeRequestIdleCallback = (cb, timeout = 100) => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(cb, { timeout });
  }
  else {
    setTimeout(cb, timeout);
  }
};

// Race condition fix: Track preload promises to prevent duplicate requests
const preloadPromises = new Map();

/**
 * Creates a preload promise for SWR
 * @param {string} key - SWR key
 * @param {Function} fetcher - Data fetcher function
 * @returns {Promise} - Promise that resolves when preload completes
 */
export const createPreloadPromise = (key, fetcher) => {
  if (!key) return Promise.resolve();

  const keyStr = JSON.stringify(key);
  if (preloadPromises.has(keyStr)) {
    return preloadPromises.get(keyStr);
  }

  const promise = fetcher()
    .then((data) => {
      mutate(key, data, { revalidate: false });
      return data;
    })
    .finally(() => {
      preloadPromises.delete(keyStr);
    });

  preloadPromises.set(keyStr, promise);
  return promise;
};

// Safe sessionStorage wrapper
const safeSessionStorage = {
  getItem: (key) => {
    try {
      return typeof Storage !== "undefined" && sessionStorage ? sessionStorage.getItem(key) : null;
    }
    catch (e) {
      console.warn("SessionStorage access failed:", e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (typeof Storage !== "undefined" && sessionStorage) {
        sessionStorage.setItem(key, value);
      }
    }
    catch (e) {
      console.warn("SessionStorage write failed:", e);
    }
  }
};

// Session storage key generators
const getSessionStorageKey = (type, id, userId) => `preloaded_${type}_${id}_${userId}`;

/**
 * Executes preload promises with error handling
 * @param {Array} promises - Array of preload promises
 * @param {string} context - Context for logging (e.g., "auth", "series")
 * @returns {Promise} - Promise that resolves when all preloads complete
 */
const executePreloads = async (promises, context = "unknown") => {
  const results = await Promise.allSettled(promises);
  const failed = results.filter(r => r.status === "rejected");

  if (failed.length > 0) {
    console.warn(`Failed to preload ${failed.length} ${context} resources:`, failed.map(f => f.reason));
  }

  return results;
};

/**
 * Preloads authentication-related data
 * @param {string} userId - User ID
 * @returns {Promise} - Promise that resolves when auth preloads complete
 */
export const preloadAuthData = async (userId) => {
  if (!userId) return;
  const { slug, invitationCode } = getSeriesInfo();
  const preloadKey = getSessionStorageKey("auth", userId, userId);
  const hasPreloaded = safeSessionStorage.getItem(preloadKey);
  if (hasPreloaded) return;

  safeSessionStorage.setItem(preloadKey, "true");

  // Add your auth preloads here when you have SWR keys
  const authPreloads = [
    // Preload auth data
    createPreloadPromise(SWR_ACCOUNT_INFO_KEY(slug, invitationCode), () => callLambda({
      method                : "get",
      service               : SERVICE_1,
      url                   : "getAccountInfo",
      queryStringParameters : { invitationCode },
    })),
  ];

  return executePreloads(authPreloads, "auth");
};

/**
 * Preloads series-specific data
 * @param {string} seriesId - Series ID
 * @param {string} userId - User ID
 * @param {Object} options - Options for preloading
 * @param {boolean} options.immediate - Whether to run immediately or defer
 * @param {boolean} options.secondary - Whether to include secondary preloads
 * @param {string} options.portalId - Portal ID for cache key matching
 * @returns {Promise} - Promise that resolves when series preloads complete
 */
export const preloadSeriesData = async (seriesId, userId, options = {}) => {
  if (!seriesId || !userId) return;

  const { immediate = true, portalId } = options;

  const preloadKey = getSessionStorageKey("series", seriesId, userId);
  const hasPreloaded = safeSessionStorage.getItem(preloadKey);
  if (hasPreloaded) return;

  safeSessionStorage.setItem(preloadKey, "true");

  // Immediate preloads
  const immediatePreloads = [
    createPreloadPromise(SWR_SERIES_PRIVATE_KEY(portalId), () => callLambda({
      method                : "get",
      service               : SERVICE_1,
      url                   : "getSeriesPrivate",
      queryStringParameters : { seriesId }
    })),
  ];

  if (immediate) {
    await executePreloads(immediatePreloads, "series immediate");
  }
};

/**
 * Preloads data for series redirects
 * @param {string} seriesId - Series ID to redirect to
 * @param {string} userId - User ID
 * @returns {Promise} - Promise that resolves when redirect preloads complete
 */
export const preloadRedirectData = async (seriesId, userId) => {
  if (!seriesId || !userId) return;

  // Start series preloads immediately for redirect
  return preloadSeriesData(seriesId, userId, {
    immediate : true,
    secondary : true
  });
};

/**
 * Unified preloader that handles all preloading scenarios
 * @param {Object} config - Configuration object
 * @param {string} config.type - Type of preloading ("auth", "series", "redirect")
 * @param {string} config.userId - User ID
 * @param {string} config.seriesId - Series ID (for series/redirect types)
 * @param {string} config.portalId - Portal ID (for series preloads cache key)
 * @param {Object} config.options - Additional options
 * @returns {Promise} - Promise that resolves when preloads complete
 */
export const preloadData = async (config) => {
  const { type, userId, seriesId, portalId, options = {} } = config;

  switch (type) {
  case "auth":
    return preloadAuthData(userId);

  case "series":
    return preloadSeriesData(seriesId, userId, { ...options, portalId });

  case "redirect":
    return preloadRedirectData(seriesId, userId);

  default:
    console.warn(`Unknown preload type: ${type}`);
    return Promise.resolve();
  }
};

export default {
  safeRequestIdleCallback,
  createPreloadPromise,
  preloadAuthData,
  preloadSeriesData,
  preloadRedirectData,
  preloadData
};
